import type { Spell } from "../types/spells.js";
import type { Position } from "../types/grid.js";
import type {
  InteractionDispatcher,
  DispatchContext,
} from "../renderer/InteractionDispatcher.js";

// ── Resolved targets ──────────────────────────────────────────────────────────

/**
 * The shape the cast resolver receives once the player has finished picking.
 * Discriminated by `kind` — covers every value of `SpellTargeting`.
 */
export type ResolvedTarget =
  | { readonly kind: "self"; readonly casterId: string }
  | { readonly kind: "single"; readonly instanceId: string }
  | { readonly kind: "touch"; readonly instanceId: string }
  | { readonly kind: "aoe"; readonly cell: Position }
  | { readonly kind: "multi"; readonly instanceIds: readonly string[] };

/** State exposed to the UI while a spell is armed and waiting for a target. */
export interface ArmedSpell {
  readonly spell: Spell;
  readonly casterId: string;
  /** Multi-target only: the instance ids picked so far (unique, ordered). */
  readonly selectedSoFar: readonly string[];
}

// ── Callbacks injected by the engine layer ────────────────────────────────────

export type CommitFn = (
  spell: Spell,
  casterId: string,
  target: ResolvedTarget,
) => void;

/** Hover-preview callback for AoE mode (`previewCell`). UI paints the splash. */
export type PreviewFn = (cell: Position | null, spell: Spell) => void;

// ── Dispatcher ────────────────────────────────────────────────────────────────

/**
 * Targeting-mode dispatcher (S06).
 *
 * Sits in front of the cast resolver. Once the UI calls `arm(spell, casterId)`
 * the dispatcher routes subsequent clicks/hovers based on `spell.targeting`:
 *
 *   - `self`   → auto-commit on `arm`; never enters the armed state
 *   - `single` → next entity click commits
 *   - `touch`  → next entity click commits (range/adjacency check happens
 *                inside the resolver, not here — keeps this module pure)
 *   - `aoe`    → entity clicks ignored; `previewCell` updates the hover paint;
 *                `commitOnCell` finalises
 *   - `multi`  → each entity click toggles selection; `finalize()` commits
 *
 * The dispatcher does **not** consume slots, roll attacks, or talk to the
 * EventBus — those are the cast-resolver's job (S05/S07/etc). Keeping this
 * module callback-driven leaves later spell-system tasks free to plug in
 * without coupling to a specific event surface.
 */
export class SpellTargetingDispatcher {
  private armed: ArmedSpell | null = null;

  constructor(
    private readonly onCommit: CommitFn,
    private readonly onPreview: PreviewFn = () => {},
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Begin targeting `spell` for `casterId`. Returns:
   *   - `"committed"`   when the spell needed no further input (self mode)
   *   - `"armed"`       when the dispatcher is now waiting for a click
   * Re-arming while a spell is already armed cancels the previous one
   * (single live targeting session).
   */
  arm(spell: Spell, casterId: string): "armed" | "committed" {
    if (this.armed) this.cancel();
    if (spell.targeting === "self") {
      this.onCommit(spell, casterId, { kind: "self", casterId });
      return "committed";
    }
    this.armed = { spell, casterId, selectedSoFar: [] };
    return "armed";
  }

  /** Drop the active session. Clears the AoE preview if one is showing. */
  cancel(): void {
    if (this.armed?.spell.targeting === "aoe") {
      this.onPreview(null, this.armed.spell);
    }
    this.armed = null;
  }

  /** True when a spell is waiting for the player to pick a target. */
  isArmed(): boolean {
    return this.armed !== null;
  }

  /** Snapshot of the current armed state — for UI rendering. */
  getArmed(): ArmedSpell | null {
    return this.armed;
  }

  // ── Click commits ──────────────────────────────────────────────────────────

  /**
   * Process a click on an entity (enemy or party slot). Returns `true` when
   * the click was consumed by targeting and `false` when the click should
   * fall through to other dispatcher rules (no spell armed, or wrong mode
   * for this kind of click).
   */
  commitOnEntity(target: { instanceId: string }): boolean {
    if (!this.armed) return false;
    const { spell, casterId } = this.armed;
    switch (spell.targeting) {
      case "single": {
        this.onCommit(spell, casterId, {
          kind: "single",
          instanceId: target.instanceId,
        });
        this.armed = null;
        return true;
      }
      case "touch": {
        this.onCommit(spell, casterId, {
          kind: "touch",
          instanceId: target.instanceId,
        });
        this.armed = null;
        return true;
      }
      case "multi": {
        const sofar = this.armed.selectedSoFar;
        const next = sofar.includes(target.instanceId)
          ? sofar.filter((id) => id !== target.instanceId)
          : [...sofar, target.instanceId];
        this.armed = { ...this.armed, selectedSoFar: next };
        return true;
      }
      case "self":
      case "aoe":
        // Wrong target type for the active mode — the click is not for us.
        return false;
    }
  }

  /**
   * Process a click on a floor cell. Only meaningful for AoE — single/touch/multi
   * spells expect entity clicks. `self` never reaches this path.
   */
  commitOnCell(cell: Position): boolean {
    if (!this.armed) return false;
    if (this.armed.spell.targeting !== "aoe") return false;
    const { spell, casterId } = this.armed;
    this.onCommit(spell, casterId, { kind: "aoe", cell });
    this.armed = null;
    return true;
  }

  /**
   * AoE-only hover update. Forwards to the preview callback so the renderer
   * can paint the splash overlay on the highlighted cells.
   */
  previewCell(cell: Position): void {
    if (this.armed?.spell.targeting === "aoe") {
      this.onPreview(cell, this.armed.spell);
    }
  }

  /**
   * Confirm the running multi-target selection. Returns `true` on commit.
   * No-op (returns `false`) if not armed in multi mode or the selection is empty.
   */
  finalize(): boolean {
    if (!this.armed || this.armed.spell.targeting !== "multi") return false;
    if (this.armed.selectedSoFar.length === 0) return false;
    const { spell, casterId, selectedSoFar } = this.armed;
    this.onCommit(spell, casterId, {
      kind: "multi",
      instanceIds: [...selectedSoFar],
    });
    this.armed = null;
    return true;
  }

  // ── F07 plug-in ────────────────────────────────────────────────────────────

  /**
   * Attach a high-priority rule to the F07 `InteractionDispatcher` that
   * intercepts entity clicks while a spell is armed. Returns an unregister
   * handle.
   *
   * The rule fires only when armed *and* the click landed on an
   * enemy/npc/party entity — AoE cell picking happens through a separate
   * code path (`commitOnCell`) since it requires raycasting against the
   * floor mesh, not the interactive layer.
   */
  installInto(dispatcher: InteractionDispatcher): () => void {
    return dispatcher.registerHighPriority(
      (ctx: DispatchContext) =>
        this.armed !== null && isEntityKind(ctx.hit.kind),
      (ctx: DispatchContext) => {
        this.commitOnEntity({ instanceId: ctx.hit.instanceId });
      },
    );
  }
}

function isEntityKind(kind: string | undefined): boolean {
  return kind === "enemy" || kind === "npc" || kind === "party";
}
